/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useEffect, useState} from 'react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    useCustomerProductLists,
    useShopperCustomersMutation,
    useAccessToken
} from '@salesforce/commerce-sdk-react'
import {useCommerceApi} from '@salesforce/commerce-sdk-react'

const onClient = typeof window !== 'undefined'
// TODO: remove `listId` input -> use the first list of type wish_list instead
// (mimic the logic in the other older hook 'use-wishlist.js')
export const useWishList = ({listId = ''} = {}) => {
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()

    const createCustomerProductList = useShopperCustomersMutation('createCustomerProductList')
    const {data: productLists, ...restOfQuery} = useCustomerProductLists(
        {
            parameters: {customerId}
        },
        {
            onSuccess: (data) => {
                if (!data.total) {
                    createCustomerProductList.mutate({
                        parameters: {customerId},
                        // we only use one type of product lists for now
                        body: {type: 'wish_list'}
                    })
                }
            },
            enabled: onClient && Boolean(customerId)
        }
    )

    const wishLists = productLists?.data?.filter((list) => list.type === 'wish_list') || []
    const currentWishlist = wishLists.find((list) => list.id === listId)
    const wishListData = listId ? currentWishlist : wishLists[0]

    // `customerProductListItems` includes offline products
    const wishlistedProductIds =
        wishListData?.customerProductListItems?.map((item) => item.productId) ?? []

    // `useProducts` doesn't return data for offline products
    const [productsData, setProductsData] = useState({data: []})
    const [isProductsLoading, setIsProductsLoading] = useState(false)

    // to bypass limit of 24 products we make multiple calls to getProducts if
    // there are more than 24 products in customer wishlist
    useEffect(() => {
        const fetchProducts = async () => {
            if (!wishlistedProductIds?.length) {
                setProductsData({data: []})
                return
            }
            const token = await getTokenWhenReady()
            setIsProductsLoading(true)

            const chunks = chunk(wishlistedProductIds, 24)
            const allProducts = []

            for (const idsChunk of chunks) {
                const response = await api.shopperProducts.getProducts({
                    parameters: {
                        ids: idsChunk.join(','),
                        allImages: true
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })

                if (response?.data) {
                    allProducts.push(...response.data)
                }
            }

            setProductsData({data: allProducts})
            setIsProductsLoading(false)
        }

        fetchProducts()
    }, [wishlistedProductIds.join(',')])

    // Filtered `customerProductListItems` that only includes online products
    const onlineCustomerProductListItems = wishListData?.customerProductListItems?.filter((item) =>
        productsData?.data?.find((product) => product.id === item.productId)
    )

    return {
        data: wishListData,
        ...restOfQuery,
        derivedData: {
            isLoading: isProductsLoading && wishlistedProductIds?.length > 0,
            onlineWishlistProductItems: productsData?.data ?? [],
            onlineCustomerProductListItems: onlineCustomerProductListItems ?? []
        }
    }
}

/**
 * Splits an iterable or array-like object into smaller arrays ("chunks") of a specified size.
 * @param {Array|Iterable} chunk - The array or iterable object to be divided into chunks.
 * @returns {Array<Array>} An array containing the chunked sub-arrays.
 */
function chunk(array, size) {
    const result = []
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size))
    }
    return result
}
